import { apiClient } from "./base.service";
import type { 
  CustomerProjection,
  CustomerGrowthScenario,
  AcquisitionCostModel,
  SubscriptionTier,
  CustomerCapacityGroup,
  CreateCustomerProjectionData,
  CreateSubscriptionTierData,
  CreateCustomerGrowthScenarioData,
  CreateAcquisitionCostModelData,
  CreateCustomerCapacityGroupData,
} from '../../types/startup.types';

// Export the types so they can be imported by other modules
export type {
  CreateCustomerProjectionData,
  CreateSubscriptionTierData,
  CreateCustomerGrowthScenarioData,
  CreateAcquisitionCostModelData,
  CreateCustomerCapacityGroupData,
};

export class CustomerAPIService {
  private async getAuthHeaders() {
    const { getAuth } = await import("firebase/auth");
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  // Customer Projections
  async getCustomerProjections(startupId: string): Promise<CustomerProjection[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/customer-projections`, { headers });
  }

  async createCustomerProjection(
    startupId: string,
    data: CreateCustomerProjectionData,
  ): Promise<CustomerProjection> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/customer-projections`, data, { headers });
  }

  async updateCustomerProjection(
    startupId: string,
    projectionId: string,
    data: Partial<CreateCustomerProjectionData>,
  ): Promise<CustomerProjection> {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}/customer-projections/${projectionId}`, data, {
      headers,
    });
  }

  async deleteCustomerProjection(
    startupId: string,
    projectionId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/customer-projections/${projectionId}`, {
      headers,
    });
  }

  // Subscription Tiers
  async getSubscriptionTiers(startupId: string): Promise<SubscriptionTier[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/subscription-tiers`, { headers });
  }

  async createSubscriptionTier(
    startupId: string,
    data: CreateSubscriptionTierData,
  ): Promise<SubscriptionTier> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/subscription-tiers`, data, { headers });
  }

  async updateSubscriptionTier(
    startupId: string,
    tierId: string,
    data: Partial<CreateSubscriptionTierData>,
  ): Promise<SubscriptionTier> {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}/subscription-tiers/${tierId}`, data, {
      headers,
    });
  }

  async deleteSubscriptionTier(
    startupId: string,
    tierId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/subscription-tiers/${tierId}`, {
      headers,
    });
  }

  // Customer Growth Scenarios
  async getCustomerGrowthScenarios(startupId: string): Promise<CustomerGrowthScenario[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/customer-growth-scenarios`, { headers });
  }

  async createCustomerGrowthScenario(
    startupId: string,
    data: CreateCustomerGrowthScenarioData,
  ): Promise<CustomerGrowthScenario> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/customer-growth-scenarios`, data, { headers });
  }

  // Acquisition Cost Models
  async getAcquisitionCostModels(startupId: string): Promise<AcquisitionCostModel[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/acquisition-cost-models`, { headers });
  }

  async createAcquisitionCostModel(
    startupId: string,
    data: CreateAcquisitionCostModelData,
  ): Promise<AcquisitionCostModel> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/acquisition-cost-models`, data, { headers });
  }

  async getCapacityGroups(startupId: string): Promise<CustomerCapacityGroup[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/capacity-groups`, { headers });
  }

  async getCapacityGroup(
    startupId: string,
    groupId: string,
  ): Promise<
    CustomerCapacityGroup & {
      linkedProjections: CustomerProjection[];
      capacityEvents: Array<Record<string, unknown>>;
    }
  > {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/capacity-groups/${groupId}`, {
      headers,
    });
  }

  async createCapacityGroup(
    startupId: string,
    data: CreateCustomerCapacityGroupData,
  ): Promise<CustomerCapacityGroup> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/capacity-groups`, data, { headers });
  }

  async updateCapacityGroup(
    startupId: string,
    groupId: string,
    data: Partial<CreateCustomerCapacityGroupData>,
  ): Promise<CustomerCapacityGroup> {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}/capacity-groups/${groupId}`, data, {
      headers,
    });
  }

  async deleteCapacityGroup(
    startupId: string,
    groupId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/capacity-groups/${groupId}`, {
      headers,
    });
  }
}

export const customerAPI = new CustomerAPIService();